import { type GridPaginationModel, type GridSortModel } from '@mui/x-data-grid';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { getScrollableAncestors, type ScrollSnapshot } from './scrollRetention';

type UseGridScrollRetentionParams = {
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  setSortModelInternal: (model: GridSortModel) => void;
  paginationPage: number;
  queryIsFetching: boolean;
  queryDataUpdatedAt: number;
};

type UseGridScrollRetentionResult = {
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
  captureScrollAnchor: () => void;
};

export const useGridScrollRetention = ({
  setPaginationModel,
  setSortModelInternal,
  paginationPage,
  queryIsFetching,
  queryDataUpdatedAt,
}: UseGridScrollRetentionParams): UseGridScrollRetentionResult => {
  const pendingScrollRef = useRef<number | null>(null);
  const pendingScrollXRef = useRef<number | null>(null);
  const pendingAncestorSnapshotsRef = useRef<ScrollSnapshot[]>([]);
  const pendingPageRef = useRef<number | null>(null);
  const pendingScrollTypeRef = useRef<'pagination' | 'sort' | null>(null);
  const sortRestoreTimeoutRef = useRef<number | null>(null);
  const scrollLockRef = useRef<{ anchor: number; until: number } | null>(null);
  const scrollLockRafRef = useRef<number | null>(null);

  const maybeRestoreScroll = useCallback(
    (targetY: number | null, targetX: number | null, ancestorSnapshots: ScrollSnapshot[]) => {
      if (targetY === null || targetX === null) return;
      const currentY = typeof window !== 'undefined' ? window.scrollY : null;
      const currentX = typeof window !== 'undefined' ? window.scrollX : null;
      if (currentY === null || currentX === null) return;

      ancestorSnapshots.forEach(({ element, top, left }) => {
        element.scrollTop = top;
        element.scrollLeft = left;
      });

      if (Math.abs(currentY - targetY) <= 2 && Math.abs(currentX - targetX) <= 2) return;
      window.scrollTo({ top: targetY, left: targetX, behavior: 'auto' });
    },
    [],
  );

  const runScrollLock = useCallback(() => {
    if (!scrollLockRef.current) {
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
        scrollLockRafRef.current = null;
      }
      return;
    }
    const { anchor, until } = scrollLockRef.current;
    if (Date.now() > until) {
      scrollLockRef.current = null;
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
        scrollLockRafRef.current = null;
      }
      return;
    }
    maybeRestoreScroll(anchor, pendingScrollXRef.current, pendingAncestorSnapshotsRef.current);
    scrollLockRafRef.current = window.requestAnimationFrame(runScrollLock);
  }, [maybeRestoreScroll]);

  const startScrollLock = useCallback(
    (anchor: number) => {
      scrollLockRef.current = { anchor, until: Date.now() + 300 };
      scrollLockRafRef.current ??= window.requestAnimationFrame(runScrollLock);
    },
    [runScrollLock],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
      const scrollX = typeof window !== 'undefined' ? window.scrollX : null;
      const activeElement =
        typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const ancestorSnapshots = getScrollableAncestors(activeElement);
      activeElement?.blur();
      setPaginationModel(model);
      if (scrollY === null || scrollX === null) return;
      pendingScrollRef.current = scrollY;
      pendingScrollXRef.current = scrollX;
      pendingAncestorSnapshotsRef.current = ancestorSnapshots;
      pendingPageRef.current = model.page;
      pendingScrollTypeRef.current = 'pagination';
      startScrollLock(scrollY);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
        });
      }
    },
    [maybeRestoreScroll, setPaginationModel, startScrollLock],
  );

  const captureScrollAnchor = useCallback(() => {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    const scrollX = typeof window !== 'undefined' ? window.scrollX : null;
    const ancestorSnapshots =
      typeof document !== 'undefined' ? getScrollableAncestors(document.activeElement) : [];
    if (scrollY === null || scrollX === null) return;
    pendingScrollRef.current = scrollY;
    pendingScrollXRef.current = scrollX;
    pendingAncestorSnapshotsRef.current = ancestorSnapshots;
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = 'sort';
    startScrollLock(scrollY);
  }, [startScrollLock]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
      const scrollX = typeof window !== 'undefined' ? window.scrollX : null;
      const ancestorSnapshots =
        typeof document !== 'undefined' ? getScrollableAncestors(document.activeElement) : [];
      setSortModelInternal(model);
      if (scrollY === null || scrollX === null) return;
      pendingScrollRef.current = scrollY;
      pendingScrollXRef.current = scrollX;
      pendingAncestorSnapshotsRef.current = ancestorSnapshots;
      pendingPageRef.current = null;
      pendingScrollTypeRef.current = 'sort';
      startScrollLock(scrollY);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
        });
      }
      if (sortRestoreTimeoutRef.current !== null) {
        window.clearTimeout(sortRestoreTimeoutRef.current);
      }
      sortRestoreTimeoutRef.current = window.setTimeout(() => {
        maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
        sortRestoreTimeoutRef.current = null;
      }, 120);
    },
    [maybeRestoreScroll, setSortModelInternal, startScrollLock],
  );

  useLayoutEffect(() => {
    if (pendingScrollRef.current === null) return;
    if (queryIsFetching) return;
    if (pendingPageRef.current !== null && pendingPageRef.current !== paginationPage) return;
    if (pendingScrollTypeRef.current === 'sort') return;
    const scrollY = pendingScrollRef.current;
    const scrollX = pendingScrollXRef.current;
    const ancestorSnapshots = pendingAncestorSnapshotsRef.current;
    pendingScrollRef.current = null;
    pendingScrollXRef.current = null;
    pendingAncestorSnapshotsRef.current = [];
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = null;
    window.setTimeout(() => {
      maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
    }, 0);
  }, [maybeRestoreScroll, paginationPage, queryIsFetching]);

  useLayoutEffect(() => {
    if (pendingScrollRef.current === null) return;
    if (queryIsFetching) return;
    if (pendingScrollTypeRef.current === 'sort') {
      const scrollY = pendingScrollRef.current;
      const scrollX = pendingScrollXRef.current;
      const ancestorSnapshots = pendingAncestorSnapshotsRef.current;
      pendingScrollRef.current = null;
      pendingScrollXRef.current = null;
      pendingAncestorSnapshotsRef.current = [];
      pendingPageRef.current = null;
      pendingScrollTypeRef.current = null;
      maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
      scrollLockRef.current = null;
      return;
    }
    const scrollY = pendingScrollRef.current;
    const scrollX = pendingScrollXRef.current;
    const ancestorSnapshots = pendingAncestorSnapshotsRef.current;
    pendingScrollRef.current = null;
    pendingScrollXRef.current = null;
    pendingAncestorSnapshotsRef.current = [];
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = null;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
      });
    } else {
      window.setTimeout(() => {
        maybeRestoreScroll(scrollY, scrollX, ancestorSnapshots);
      }, 0);
    }
  }, [maybeRestoreScroll, queryDataUpdatedAt, queryIsFetching]);

  useEffect(() => {
    return () => {
      if (sortRestoreTimeoutRef.current !== null) {
        window.clearTimeout(sortRestoreTimeoutRef.current);
      }
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
      }
    };
  }, []);

  return {
    handlePaginationModelChange,
    handleSortModelChange,
    captureScrollAnchor,
  };
};
